import * as React from 'react';
/**
 * @interface ButtonProps
 * @description Define las propiedades del componente Button.
 */
interface ButtonProps {
  /**
   * @property {string} type - El tipo del button.
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * @property {React.ReactNode} children - Los hijos del button.
   */
  children: React.ReactNode;
  /**
   * @property {string} variant - La variante del button.
   */
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'ghost';
  /**
   * @property {function} onClick - La función de clic del button.
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * @property {string} size - El tamaño del button.
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * @property {boolean} loading - Indica si el button está en estado de carga.
   */
  loading?: boolean;
  /**
   * @property {boolean} fullWidth - Indica si el button debe ocupar todo el ancho.
   */
  fullWidth?: boolean;
  /**
   * @property {string} className - La clase del button.
   */
  className?: string;
  /**
   * @property {boolean} disabled - Indica si el button está deshabilitado.
   */
  disabled?: boolean;
  /**
   * @property {string} text - Informacion del button.
   */
  text?: string;
}
/**
 * @component Botón
 * @description Un componente de botón personalizable con diferentes variantes, tamaños y un estado de carga.
 * @param props Las propiedades del componente.
 */
export const Button: React.FC<ButtonProps> = ({
  type = 'button',
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  className = '',
  disabled,
  onClick,
  ...props
}) => {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    primary: 'bg-blue-600  text-text-secondary hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-gray-600  text-text-secondary hover:bg-gray-700 focus:ring-gray-500',
    success: 'bg-success-600  text-text-secondary hover:bg-success-700 focus:ring-success-500',
    warning:
      'bg-yellow-600  text-text-secondary hover:bg-yellow-700 focus:ring-yellow-500',
    error: 'bg-error-600  text-text-secondary hover:bg-error-700 focus:ring-error-500',
    ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const widthClasses = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`cursor-pointer ${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClasses} ${className}`}
      disabled={disabled || loading}
      type={type}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <svg
          className="mr-2 -ml-1 w-4 h-4 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};


